// Compra Informativa (DA-309) — fornecedor UNICO, lista chapada, sem comparacao de preco.
// Serve pra REGISTRAR o que esta sendo comprado quando so ha um fornecedor possivel.
//
// O que a torna diferente da cotacao comparativa (`CotacaoRespostas`):
//   - o operador digita o CODIGO DO FORNECEDOR e o item vem do Catalogo do Fornecedor (o cadastro do
//     lado do fornecedor), NAO do nosso cadastro de produtos;
//   - o item pode ser um SKU NOVO, que ainda nao existe na loja: nao achou o codigo, digita a
//     descricao e segue. O produto nasce depois, sozinho, quando o XML da nota chegar.
// Mesma tela para criar (sem :id) e editar (com :id).
import { useCallback, useEffect, useRef, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, Trash2, FileDown, ClipboardList, ChevronUp, ChevronDown } from 'lucide-react';
import { api, ApiError, getToken } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { Modal } from '../../components/Modal';
import { FotoProduto } from '../../components/FotoProduto';
import { ModalPdfPronto, type PdfPronto } from '../../components/ModalPdfPronto';
import { escolherArquivoPdf, gravarPdf } from '../../lib/salvar-pdf';

interface Fornecedor {
  id: number;
  razao_social: string | null;
  nome_fantasia: string | null;
  cidade?: string | null;
  uf?: string | null;
}

interface ItemCatalogo {
  /** o item da EMPRESA (o pai) — sempre existe quando o codigo casa (DA-310). */
  item_id: number;
  /** a PERNA da filial consultada. NULL quando a filial ainda nao tem oferta: o item existe (a
   *  irma cadastrou), so nao ha preco nesta filial — a linha entra sem preco, nao em branco. */
  id: number | null;
  produto_id: number | null;
  codigo_fornecedor: string | null;
  descricao_fornecedor: string | null;
  marca: string | null;
  embalagem: string | null;
  quantidade_embalagem: string | null;
  preco_unitario: string | null;
  ncm: string | null;
  produto_codigo: string | null;
  produto_descricao: string | null;
  foto_url: string | null;
  unidade_sigla: string | null;
}

// Linha da tela. Strings nos campos digitaveis (pt-BR), numero so na hora de somar/enviar.
interface Linha {
  key: string;
  produto_id: number | null;
  produto_codigo: string | null;
  codigo_fornecedor: string;
  descricao: string;
  marca: string;
  embalagem: string;
  quantidade_embalagem: string;
  preco_embalagem: string;
  quantidade: string;
  foto_url: string | null;
  ncm: string | null;
  novo: boolean; // codigo nao existia no catalogo — SKU novo
}

const num = (v: string): number => {
  const n = parseFloat(String(v ?? '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const fmtDin = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (v: unknown, casas = 2): string => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  if (!Number.isFinite(n) || n === 0) return '';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: casas });
};

export function CompraInformativa() {
  const navigate = useNavigate();
  const params = useParams();
  // Voltar honra de onde a tela foi aberta (ex.: "O que falta comprar" passa state.from).
  const location = useLocation();
  const voltarPara = (location.state as { from?: string } | null)?.from ?? '/cotacoes';
  const toast = useToast();
  const dialog = useDialog();

  const [cotacaoId, setCotacaoId] = useState<number | null>(params.id ? Number(params.id) : null);
  const [numero, setNumero] = useState('');
  const [fornecedor, setFornecedor] = useState<Fornecedor | null>(null);
  const [observacoes, setObservacoes] = useState('');
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [carregando, setCarregando] = useState(!!params.id);
  const [salvando, setSalvando] = useState(false);
  const [baixandoPdf, setBaixandoPdf] = useState(false);
  const [pdfPronto, setPdfPronto] = useState<PdfPronto | null>(null);
  const [sujo, setSujo] = useState(false);
  // Linha que esta sendo editada agora (tem algum campo com foco) -> pinta toda de amarelo pra
  // o operador nao perder de vista onde esta. Aqui o amarelo nao significa outra coisa (nao ha o
  // "melhor preco" da comparativa), entao pode ser o destaque padrao (#fde68a + preto, daltonismo).
  const [linhaAtiva, setLinhaAtiva] = useState<string | null>(null);

  const [codigo, setCodigo] = useState('');
  const [buscandoCodigo, setBuscandoCodigo] = useState(false);
  const codigoRef = useRef<HTMLInputElement>(null);

  const [modalForn, setModalForn] = useState(false);
  const [listaForn, setListaForn] = useState<Fornecedor[]>([]);
  const [buscaForn, setBuscaForn] = useState('');
  const [carregandoForn, setCarregandoForn] = useState(false);
  const fornTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const nomeForn = (f: Fornecedor | null) => (f ? f.nome_fantasia || f.razao_social || `#${f.id}` : '');

  // ---------- carga (modo edicao) ----------
  const carregar = useCallback(async (id: number) => {
    try {
      setCarregando(true);
      const c = await api.get<any>(`/api/cotacoes/${id}`);
      setNumero(c.numero || '');
      setObservacoes(c.observacoes || '');
      const f = (c.fornecedores || [])[0];
      if (f) setFornecedor({ id: f.fornecedor_id, razao_social: f.razao_social, nome_fantasia: f.nome_fantasia });
      const respPorItem = new Map<number, any>();
      for (const r of c.respostas || []) respPorItem.set(Number(r.cotacao_item_id), r);
      setLinhas((c.itens || []).map((it: any, i: number) => {
        const r = respPorItem.get(Number(it.id)) || {};
        return {
          key: `l${it.id}-${i}`,
          produto_id: it.produto_id ?? null,
          produto_codigo: it.codigo_interno ?? null,
          codigo_fornecedor: r.cod_produto_fornecedor || '',
          descricao: it.produto_descricao || r.descricao_complementar || '',
          marca: r.marca || it.produto_marca || '',
          embalagem: r.embalagem || '',
          quantidade_embalagem: fmtNum(r.quantidade_embalagem, 3),
          preco_embalagem: fmtNum(r.preco_principal, 4),
          quantidade: fmtNum(it.quantidade, 3) || '1',
          foto_url: it.foto_url ?? null,
          ncm: null,
          novo: it.produto_id == null,
        } as Linha;
      }));
      setSujo(false);
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao carregar a compra');
    } finally { setCarregando(false); }
  }, [toast]);

  useEffect(() => {
    if (params.id) void carregar(Number(params.id));
    // Compra nova: a 1a coisa que a tela precisa e o fornecedor (sem ele o campo de codigo nao tem
    // onde procurar). Em vez de deixar o operador procurar o botao, o seletor ja abre sozinho.
    else setModalForn(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  // ---------- fornecedor ----------
  useEffect(() => {
    if (!modalForn) return;
    if (fornTimer.current) clearTimeout(fornTimer.current);
    const termo = buscaForn.trim();
    fornTimer.current = setTimeout(async () => {
      try {
        setCarregandoForn(true);
        setListaForn(await api.get<Fornecedor[]>('/api/fornecedores', { query: { busca: termo || undefined } }));
      } catch { toast.erro('Erro ao carregar fornecedores'); }
      finally { setCarregandoForn(false); }
    }, 250);
    return () => { if (fornTimer.current) clearTimeout(fornTimer.current); };
  }, [modalForn, buscaForn, toast]);

  // ---------- o coracao da tela: digitar o codigo traz o item ----------
  async function buscarCodigo() {
    const cod = codigo.trim();
    if (!cod) return;
    if (!fornecedor) { toast.aviso('Escolha o fornecedor da compra primeiro.'); return; }

    // Mesmo codigo duas vezes = somar quantidade, nao duplicar linha.
    const jaTem = linhas.findIndex((l) => l.codigo_fornecedor.trim().toUpperCase() === cod.toUpperCase());
    if (jaTem >= 0) {
      setLinhas((arr) => arr.map((l, i) => i === jaTem
        ? { ...l, quantidade: String(num(l.quantidade) + 1).replace('.', ',') } : l));
      setSujo(true);
      setCodigo('');
      toast.aviso(`Codigo ${cod} ja estava na lista — somei 1 na quantidade.`);
      return;
    }

    try {
      setBuscandoCodigo(true);
      const r = await api.get<{ encontrado: boolean; codigo: string; item: ItemCatalogo | null }>(
        '/api/fornecedor-catalogo/por-codigo', { query: { fornecedor_id: fornecedor.id, codigo: cod } },
      );
      const it = r.item;
      // NAO achou = caso NORMAL do SKU novo, nao erro: a linha entra so com o codigo digitado.
      // ⚠️ Desde a [DA-638] a descricao NAO se digita aqui — ela vem do Catalogo. O foco vai
      // sempre pra QUANTIDADE, que e o proximo campo editavel dos dois casos.
      const nova: Linha = it ? {
        key: `c${it.item_id}-${Date.now()}`,
        produto_id: it.produto_id,
        produto_codigo: it.produto_codigo,
        codigo_fornecedor: it.codigo_fornecedor || r.codigo,
        descricao: it.descricao_fornecedor || it.produto_descricao || '',
        marca: it.marca || '',
        embalagem: it.embalagem || '',
        quantidade_embalagem: fmtNum(it.quantidade_embalagem, 3),
        preco_embalagem: fmtNum(it.preco_unitario, 4),
        quantidade: '1',
        foto_url: it.foto_url,
        ncm: it.ncm,
        novo: false,
      } : {
        key: `n${r.codigo}-${Date.now()}`,
        produto_id: null, produto_codigo: null,
        codigo_fornecedor: r.codigo,
        descricao: '', marca: '', embalagem: '', quantidade_embalagem: '', preco_embalagem: '',
        quantidade: '1', foto_url: null, ncm: null, novo: true,
      };
      setLinhas((arr) => [...arr, nova]);
      setSujo(true);
      setCodigo('');
      if (!it) toast.aviso(`Codigo ${r.codigo} e novo — a descricao se escreve no Catalogo do Fornecedor.`);
      setTimeout(() => {
        // ⚠️ SEMPRE `quantidade`: o campo `descricao` virou <div> na [DA-638], e `.select()` nele
        // estoura em tempo de execucao — o cast do querySelector engana o typecheck.
        const alvo = document.querySelector<HTMLInputElement>(`[data-linha="${nova.key}"][data-campo="quantidade"]`);
        alvo?.focus();
        alvo?.select();
      }, 30);
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao buscar o codigo');
    } finally {
      setBuscandoCodigo(false);
      codigoRef.current?.focus();
    }
  }

  function editar(key: string, campo: keyof Linha, valor: string) {
    setLinhas((arr) => arr.map((l) => (l.key === key ? { ...l, [campo]: valor } : l)));
    setSujo(true);
  }

  // Reposiciona a linha uma casa pra cima/baixo. A ordem so vive no array `linhas` — o Salvar
  // grava `cotacao_itens.ordem` pelo indice (o GET rele por essa coluna), entao mexer no array
  // e marcar sujo basta; persiste junto com o resto ao Salvar.
  function moverLinha(idx: number, delta: number) {
    const to = idx + delta;
    if (to < 0 || to >= linhas.length) return;
    setLinhas((arr) => {
      const novos = [...arr];
      const [movido] = novos.splice(idx, 1);
      novos.splice(to, 0, movido!);
      return novos;
    });
    setSujo(true);
  }

  async function remover(l: Linha) {
    const ok = await dialog.confirmar(`Remover "${l.descricao || l.codigo_fornecedor}" da compra?`, { perigoso: true });
    if (!ok) return;
    setLinhas((arr) => arr.filter((x) => x.key !== l.key));
    setSujo(true);
  }

  const totalGeral = linhas.reduce((s, l) => s + num(l.preco_embalagem) * num(l.quantidade), 0);

  // ---------- salvar ----------
  async function salvar(): Promise<number | null> {
    if (!fornecedor) { toast.erro('Escolha o fornecedor da compra.'); return null; }
    // ⛔ A exigencia de descricao CAIU junto com o campo (decisao do Ruan, 28/08/2026): a descricao
    // agora vem do Catalogo, e recusar o Salvar por um campo que ninguem pode mais preencher seria
    // beco sem saida. A identidade da linha segue cobrada no backend (codigo do fornecedor OU
    // produto do cadastro) — o que se perdeu foi so o texto, que se escreve no Catalogo.
    try {
      setSalvando(true);
      let id = cotacaoId;
      if (!id) {
        const nova = await api.post<{ id: number; numero: string }>('/api/cotacoes', {
          tipo: 'informativa',
          observacoes: observacoes.trim() ? observacoes.trim().toUpperCase() : null,
          fornecedor_ids: [fornecedor.id],
        });
        id = nova.id;
        setCotacaoId(id);
        setNumero(nova.numero);
      }
      await api.put(`/api/cotacoes/${id}/informativa`, {
        fornecedor_id: fornecedor.id,
        observacoes: observacoes.trim() ? observacoes.trim().toUpperCase() : null,
        itens: linhas.map((l) => ({
          produto_id: l.produto_id,
          codigo_fornecedor: l.codigo_fornecedor,
          descricao: l.descricao.trim(),
          marca: l.marca.trim() || null,
          embalagem: l.embalagem.trim() || null,
          quantidade_embalagem: num(l.quantidade_embalagem) || 1,
          preco_embalagem: num(l.preco_embalagem) || null,
          quantidade: num(l.quantidade) || 1,
          ncm: l.ncm,
        })),
      });
      setSujo(false);
      toast.sucesso('Compra registrada');
      if (!params.id && id) navigate(`/cotacoes/${id}/informativa`, { replace: true });
      return id;
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao salvar');
      return null;
    } finally { setSalvando(false); }
  }

  // O PDF le o que esta GRAVADO — salva antes, senao sai o pedido de ontem.
  // Baixa com fetch (e nao window.open) porque o endpoint exige o token no header.
  // `baixandoPdf` cobre a operacao INTEIRA (salvar + gerar no servidor + baixar): o botao fica
  // desabilitado e mostra "Gerando..." o tempo todo. Antes so `salvando` travava o botao, entao
  // durante a geracao do PDF (Puppeteer, alguns segundos) ele voltava a clicavel sem nenhum sinal
  // e o operador clicava de novo achando que nao pegou.
  // O "Salvar como" abre ANTES de salvar e gerar: a janela so abre colada ao clique
  // (lib/salvar-pdf.ts). Cancelou = nem salva nem gera.
  async function baixarPdf() {
    if (baixandoPdf) return;
    try {
      setBaixandoPdf(true);
      const arquivo = `pedido-compra-${numero || cotacaoId || 'nova'}.pdf`;
      const destino = await escolherArquivoPdf(arquivo);
      if (destino === 'cancelado') return;
      const id = sujo || !cotacaoId ? await salvar() : cotacaoId;
      if (!id || !fornecedor) return;
      const token = getToken();
      const res = await fetch(`/api/cotacoes/${id}/pedido-compra/${fornecedor.id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error('Nao foi possivel gerar o PDF');
      const blob = await res.blob();
      if (destino) {
        try {
          await gravarPdf(destino, blob);
          toast.sucesso(`PDF salvo: ${destino.name}`);
          return;
        } catch {
          // O PDF ja foi gerado: vai pra janela "PDF pronto" em vez de se perder.
          toast.erro(`Nao consegui gravar em ${destino.name} — baixe pela janela.`);
        }
      }
      // Sem download automatico: depois da geracao o clique ja nao conta como gesto e o Chrome
      // pode descartar calado (DA-571, 22/10/2026). O botao da janela e' um clique novo.
      const url = URL.createObjectURL(blob);
      setPdfPronto((anterior) => {
        if (anterior) URL.revokeObjectURL(anterior.url);
        return { url, arquivo, fotosFalharam: 0 };
      });
    } catch (e: unknown) {
      toast.erro(e instanceof Error ? e.message : 'Erro ao gerar o PDF');
    } finally {
      setBaixandoPdf(false);
    }
  }

  const th: React.CSSProperties = {
    padding: '6px 8px', textAlign: 'left', fontWeight: 600, fontSize: 12,
    background: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)', whiteSpace: 'nowrap',
  };
  const inp: React.CSSProperties = {
    width: '100%', padding: '3px 6px', borderRadius: 3, fontSize: 13,
    background: 'var(--input-bg)', color: 'var(--text-color)', ['--input-border' as any]: '#000',
  };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 500, color: 'var(--text-muted, #6b7280)', marginBottom: 2 };

  return (
    // Cabecalho e rodape fixos, so o miolo rola — a lista pode crescer sem empurrar os botoes
    // pra fora da tela.
    <div className="modulo-pagina" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <ModalPdfPronto pdf={pdfPronto} onFechar={() => setPdfPronto(null)} />
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--page-bg)', borderBottom: '1px solid var(--page-border, #e5e7eb)', padding: '10px 16px' }}>
        <div className="pagina-header" style={{ flexWrap: 'wrap', marginBottom: 0 }}>
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => navigate(voltarPara)}
              className="btn-icon"
              title="Voltar"
              aria-label="Voltar"
              style={{ marginRight: 4 }}
            >
              <ArrowLeft size={20} />
            </button>
            <ClipboardList size={20} />
            Compra Informativa
            {numero && <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-muted, #6b7280)' }}>{numero}</span>}
          </h2>

          <div className="pagina-acoes" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={labelStyle}>Fornecedor</span>
              <button
                type="button"
                onClick={() => setModalForn(true)}
                className="btn-secundario"
                style={{ minWidth: 220, textAlign: 'left' }}
                title="Compra informativa e de fornecedor unico"
              >
                {fornecedor ? nomeForn(fornecedor) : 'Escolher fornecedor...'}
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 240 }}>
              <span style={labelStyle}>Observacoes</span>
              <input
                type="text"
                value={observacoes}
                onChange={(e) => { setObservacoes(upperMantendoCursor(e)); setSujo(true); }}
                placeholder="Notas internas..."
                style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-color)' }}
              />
            </div>

            {sujo && (
              <span style={{ alignSelf: 'center', fontSize: 12, fontWeight: 600, color: '#f97316' }}>Alteracoes nao salvas</span>
            )}
            <button type="button" className="btn-secundario" onClick={baixarPdf} disabled={salvando || baixandoPdf || linhas.length === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <FileDown size={15} /> {baixandoPdf ? 'Gerando...' : 'Pedido de Compra'}
            </button>
            <button type="button" className="btn-primario" onClick={() => void salvar()} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding: '12px 16px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {/* A porta de entrada da tela: o codigo do FORNECEDOR (nao o nosso). */}
        <div style={{ position: 'relative', marginBottom: 12, maxWidth: 620 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--text-muted, #6b7280)' }} />
          {/* Sem fornecedor o campo nao tem onde procurar — mas ele NAO fica morto: clicar aqui
              abre o seletor de fornecedor. Campo que manda fazer algo e nao leva ate la nao ajuda. */}
          <input
            ref={codigoRef}
            type="text"
            autoFocus
            value={codigo}
            readOnly={!fornecedor}
            disabled={buscandoCodigo}
            onClick={() => { if (!fornecedor) setModalForn(true); }}
            onChange={(e) => setCodigo(e.target.value)}
            onKeyDown={(e) => {
              if (!fornecedor) { setModalForn(true); return; }
              if (e.key === 'Enter') { e.preventDefault(); void buscarCodigo(); }
            }}
            placeholder={fornecedor ? 'CODIGO DO FORNECEDOR OU INTERNO + ENTER' : 'CLIQUE PARA ESCOLHER O FORNECEDOR'}
            style={{
              width: '100%', padding: '9px 10px 9px 34px', borderRadius: 6, background: 'var(--input-bg)',
              color: 'var(--text-color)', fontSize: 14, fontWeight: 600,
              cursor: fornecedor ? 'text' : 'pointer',
            }}
          />
        </div>

        <div style={{ background: 'var(--panel-bg)', borderRadius: 6, border: '1px solid var(--panel-border, #e5e7eb)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 56, textAlign: 'center' }}>#</th>
                <th style={{ ...th, width: 52 }}>Foto</th>
                <th style={{ ...th, width: 100 }}>Codigo</th>
                <th style={th}>Descricao</th>
                <th style={{ ...th, width: 120 }}>Marca</th>
                <th style={{ ...th, width: 70 }}>Embal.</th>
                <th style={{ ...th, width: 80, textAlign: 'right' }}>Qtd na emb.</th>
                <th style={{ ...th, width: 100, textAlign: 'right' }}>Preco emb.</th>
                <th style={{ ...th, width: 70, textAlign: 'right' }}>Qtd</th>
                <th style={{ ...th, width: 100, textAlign: 'right' }}>Total</th>
                <th style={{ ...th, width: 34 }}></th>
              </tr>
            </thead>
            <tbody>
              {carregando && (
                <tr><td colSpan={11} style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Carregando...</td></tr>
              )}
              {!carregando && linhas.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>
                  Digite o codigo do fornecedor no campo acima e tecle Enter.
                </td></tr>
              )}
              {!carregando && linhas.map((l, idx) => {
                const ativa = linhaAtiva === l.key;
                // Na linha ativa os inputs herdam o amarelo: sem isso o fundo proprio do input
                // (--input-bg) cobriria a faixa e a linha nao ficaria "toda" amarela.
                const inpL: React.CSSProperties = ativa
                  ? { ...inp, ['--input-bg' as any]: '#fde68a', ['--input-fg' as any]: '#000' }
                  : inp;
                return (
                <tr
                  key={l.key}
                  onFocus={() => setLinhaAtiva(l.key)}
                  onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setLinhaAtiva((k) => (k === l.key ? null : k)); }}
                  style={{
                    borderTop: '1px solid var(--grid-row-border, #f3f4f6)',
                    ...(ativa ? { background: '#fde68a', color: '#000' } : {}),
                  }}
                >
                  <td style={{ padding: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                      <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: 12 }}>{idx + 1}</span>
                      {/* Setas pra reordenar (sem arrastar — drag trava em algumas telas). A ordem
                          persiste no Salvar. */}
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <button
                          type="button"
                          onClick={() => moverLinha(idx, -1)}
                          disabled={idx === 0}
                          title="Mover para cima"
                          style={{ background: 'transparent', border: 'none', padding: 0, lineHeight: 0, cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.25 : 1 }}
                        >
                          <ChevronUp size={15} color="var(--primary)" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moverLinha(idx, 1)}
                          disabled={idx === linhas.length - 1}
                          title="Mover para baixo"
                          style={{ background: 'transparent', border: 'none', padding: 0, lineHeight: 0, cursor: idx === linhas.length - 1 ? 'default' : 'pointer', opacity: idx === linhas.length - 1 ? 0.25 : 1 }}
                        >
                          <ChevronDown size={15} color="var(--primary)" />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    {l.foto_url ? (
                      <FotoProduto url={l.foto_url} tam={38} />
                    ) : (
                      <div style={{ width: 38, height: 38, borderRadius: 4, background: 'var(--grid-header-bg)', border: '1px solid var(--panel-border, #e5e7eb)' }} />
                    )}
                  </td>
                  <td style={{ padding: '4px 8px', fontWeight: 700, fontSize: 13 }}>
                    {l.codigo_fornecedor}
                    {/* Selo do SKU que a loja ainda nao conhece. Cinza de proposito: e um FATO da
                        linha, nao um problema — o produto nasce depois, do XML da nota. */}
                    {l.novo && (
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase' }}>
                        SKU novo
                      </div>
                    )}
                    {l.produto_codigo && (
                      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted, #6b7280)' }}>{l.produto_codigo}</div>
                    )}
                  </td>
                  {/* Descricao — SO-LEITURA (decisao do Ruan, 28/08/2026): ela vem do Catalogo do
                      Fornecedor e so se escreve la. Codigo que o Catalogo ainda nao conhece entra
                      em branco; a descricao se completa no Catalogo depois de salvar a compra. */}
                  <td style={{ padding: '4px 6px' }}>
                    <div
                      data-linha={l.key} data-campo="descricao"
                      title={l.descricao || ''}
                      style={{ padding: '3px 6px', fontSize: 12, color: 'var(--page-fg)', overflowWrap: 'anywhere' }}
                    >
                      {l.descricao || '—'}
                    </div>
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input value={l.marca} onChange={(e) => editar(l.key, 'marca', upperMantendoCursor(e))} style={inpL} />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input value={l.embalagem} onChange={(e) => editar(l.key, 'embalagem', upperMantendoCursor(e))} style={inpL} />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      inputMode="decimal" value={l.quantidade_embalagem}
                      onChange={(e) => editar(l.key, 'quantidade_embalagem', e.target.value)}
                      style={{ ...inpL, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      inputMode="decimal" value={l.preco_embalagem}
                      onChange={(e) => editar(l.key, 'preco_embalagem', e.target.value)}
                      onBlur={(e) => { const n = num(e.target.value); if (n > 0) editar(l.key, 'preco_embalagem', fmtDin(n)); }}
                      style={{ ...inpL, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      data-linha={l.key} data-campo="quantidade"
                      inputMode="decimal" value={l.quantidade}
                      onChange={(e) => editar(l.key, 'quantidade', e.target.value)}
                      style={{ ...inpL, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                    {fmtDin(num(l.preco_embalagem) * num(l.quantidade))}
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <button type="button" onClick={() => void remover(l)} title="Remover" style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2 }}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--page-border, #e5e7eb)', background: 'var(--page-bg)', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
        <span style={{ fontSize: 14, color: 'var(--text-muted, #6b7280)' }}>{linhas.length} {linhas.length === 1 ? 'item' : 'itens'}</span>
        <span style={{ fontSize: 18, fontWeight: 700 }}>Total Geral: R$ {fmtDin(totalGeral)}</span>
      </div>

      <Modal
        aberto={modalForn}
        onFechar={() => setModalForn(false)}
        titulo="Fornecedor da compra"
        largura={520}
        rodape={<button type="button" className="btn-secundario" onClick={() => setModalForn(false)}>Fechar</button>}
      >
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-muted, #6b7280)' }} />
          <input
            type="text" autoFocus placeholder="Pesquisar fornecedor..."
            value={buscaForn} onChange={(e) => setBuscaForn(e.target.value)}
            style={{ width: '100%', padding: '8px 10px 8px 34px', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-color)', fontSize: 14 }}
          />
        </div>
        {carregandoForn && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Carregando...</div>}
        {!carregandoForn && listaForn.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Nenhum fornecedor encontrado.</div>
        )}
        {!carregandoForn && listaForn.map((f) => {
          const local = [f.cidade, f.uf].filter(Boolean).join('/');
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => { setFornecedor(f); setModalForn(false); setSujo(true); setTimeout(() => codigoRef.current?.focus(), 40); }}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', textAlign: 'left',
                padding: '8px 6px', borderBottom: '1px solid var(--grid-row-border, #f3f4f6)', border: 'none',
                background: fornecedor?.id === f.id ? 'var(--menu-ativo-bg, #dbeafe)' : 'transparent',
                color: 'var(--text-color)', cursor: 'pointer',
              }}
            >
              <span style={{ fontWeight: 500 }}>{f.nome_fantasia || f.razao_social}</span>
              {local && <span style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>{local}</span>}
            </button>
          );
        })}
      </Modal>
    </div>
  );
}
